import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Grid, Box, Typography, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import TextFieldInput from "../../../components/TextFieldInput";
import SelectInput from "../../../components/SelectInput";
import CommonButtonVoucherForm from "../../../shared-components/common-button/CommonButtonVoucherForm";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import SearchableAutoComplete from "../../../components/SearchableAutoComplete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  getVoucherById,
  setActiveStep,
  setApplicantActiveStep,
  setApplicantActiveStep1,
  setApplicantActiveStep2,
  setApplicantTotal,
  setCheckFirstStepCompleted,
  setEditPreviewApplicant,
  setStepsCompleted,
  updateApplicantDetailsForm,
} from "../voucher-form.slice";
import { route } from "../../../services/apiRoutes";
import AddApplicantButton from "./AddApplicantButton";
import GoogleMapsAutocomplete from "../../../shared-components/google-maps-autocomplete/GoogleMapsAutocomplete";
import {
  BRANCH_OPTIONS,
  OCCUPATION,
  OTHERS,
} from "../../../config/constant";
import { useTranslation } from "react-i18next";
import { getLastStep } from "../../../utilis/common-funtions";
import { useVoucherFormMode } from "../../../utilis/use-voucher-form-mode";

const ProfessionalDetails = forwardRef(({ applicantNo }, ref) => {
  const dispatch = useDispatch();
  const { voucherId } = useParams();
  const history = useHistory();
  const { isApplicantUpdateMode } = useVoucherFormMode();
  const { t } = useTranslation();

  const {
    activeStep,
    masterData,
    applicantData,
    applicantActiveStep,
    stepsCompleted,
    applicantTotal,
  } = useSelector((state) => state.voucherForm);

  const applicantKey = `applicant${applicantNo}`;
  const currentApplicant = applicantData?.[applicantKey];
  const professionalDetails = currentApplicant?.professionalDetails;
  const [showAddress, setShowAddress] = useState(
    !!(
      professionalDetails?.companyAddress || professionalDetails?.companyPinCode
    )
  );
  const [showOfficeAddress, setShowOfficeAddress] = useState(
    !!(professionalDetails?.officeAddress || professionalDetails?.officePinCode)
  );

  const professionalDetailFormik = useFormik({
    initialValues: {
      occupation: "",
      industry: "",
      industryIfOthers: "",
      companyName: "",
      companyAddress: "",
      companyPinCode: "",
      annualIncome: "",
      designation: OTHERS,
      designationIfOthers: "",
      branch: "",
      rank: "",
      officeAddress: "",
      officePinCode: "",
      departmentDivision: "",
    },
    validationSchema: yup.object({
      occupation: yup
        .string()
        .required(t("professionalDetails.validation.occupationRequired")),
      industry: yup.string().when("occupation", {
        is: (occupation) =>
          [OCCUPATION.EMPLOYED_PRIVATE_SECTOR, OCCUPATION.BUSINESS].includes(
            occupation
          ),
        then: (schema) =>
          schema.required(t("professionalDetails.validation.industryRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      companyName: yup.string().when("occupation", {
        is: (occupation) =>
          [OCCUPATION.EMPLOYED_PRIVATE_SECTOR, OCCUPATION.BUSINESS].includes(
            occupation
          ),
        then: (schema) =>
          schema
            .matches(
              /^[a-zA-Z0-9\s]+$/,
              t("professionalDetails.validation.companyNamePattern")
            )
            .max(
              100,
              t("authorizedSignatoryDetails.fields.firstName.validation.max")
            )
            .required(t("professionalDetails.validation.companyNameRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      companyAddress: yup.string().notRequired(),
      companyPinCode: yup.string().notRequired(),
      annualIncome: yup
        .string()
        .required(t("professionalDetails.validation.annualIncomeRequired")),
      designationIfOthers: yup.string().when("occupation", {
        is: (occupation) =>
          [
            OCCUPATION.EMPLOYED_PRIVATE_SECTOR,
            OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR,
          ].includes(occupation),
        then: (schema) =>
          schema
            .matches(
              /^[a-zA-Z\s]+$/,
              t("professionalDetails.validation.designationIfOthersPattern")
            )
            .max(
              100,
              t("authorizedSignatoryDetails.fields.firstName.validation.max")
            )
            .required(t("professionalDetails.validation.designationRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      branch: yup.string().when("occupation", {
        is: OCCUPATION.ARMED_FORCES,
        then: (schema) =>
          schema.required(t("professionalDetails.validation.branchRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      rank: yup.string().when("occupation", {
        is: OCCUPATION.ARMED_FORCES,
        then: (schema) =>
          schema
            .matches(
              /^[a-zA-Z\s]+$/,
              t("professionalDetails.validation.rankPattern")
            )
            .max(
              100,
              t("authorizedSignatoryDetails.fields.firstName.validation.max")
            )
            .required(t("professionalDetails.validation.rankRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      officeAddress: yup.string().notRequired(),
      officePinCode: yup.string().notRequired(),
      departmentDivision: yup.string().when("occupation", {
        is: (occupation) =>
          [OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR].includes(occupation),
        then: (schema) =>
          schema
            .matches(
              /^[a-zA-Z\s]+$/,
              t("professionalDetails.validation.departmentDivision")
            )
            .max(
              100,
              t("authorizedSignatoryDetails.fields.firstName.validation.max")
            )
            .required(t("professionalDetails.validation.departmentDivision")),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: () => {},
  });

  useEffect(() => {
    if (professionalDetails) {
      professionalDetailFormik.setValues({
        occupation: professionalDetails?.occupation || "",
        annualIncome: professionalDetails?.annualIncome || "",
        industry: professionalDetails?.industry || "",
        industryIfOthers: professionalDetails?.industryIfOthers || "",
        companyName: professionalDetails?.companyName || "",
        companyAddress: professionalDetails?.companyAddress || "",
        companyPinCode: professionalDetails?.companyPinCode || "",
        designation: OTHERS,
        designationIfOthers: professionalDetails?.designationIfOthers || "",
        branch: professionalDetails?.branch || "",
        rank: professionalDetails?.rank || "",
        officeAddress: professionalDetails?.officeAddress || "",
        officePinCode: professionalDetails?.officePinCode || "",
        departmentDivision: professionalDetails?.departmentDivision || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData]);

  useEffect(() => {
    dispatch(getVoucherById(`/${voucherId}`));
  }, [dispatch, voucherId]);

  useEffect(() => {
    professionalDetailFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalDetailFormik?.values]);

  useEffect(() => {
    if (!professionalDetailFormik?.values?.companyPinCode) {
      professionalDetailFormik.setFieldValue("companyPinCode", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to create the professional details payload
  const createProfessionalDetailsPayload = (saveForLaterFlag) => {
    const values = professionalDetailFormik?.values || {};
    const occupation = values?.occupation;

    const payload = {
      opportunityId: voucherId,
      applicantNumber: Number.parseInt(applicantNo),
      lastStep: getLastStep(applicantData?.lastStep, activeStep),
      saveForLater: saveForLaterFlag,
      professionalDetails: {
        occupation,
        annualIncome: values?.annualIncome || "",
      },
    };

    switch (occupation) {
      /** Employee - Private Sector **/
      case OCCUPATION.EMPLOYED_PRIVATE_SECTOR:
        Object.assign(payload.professionalDetails, {
          industry: values?.industry || "",
          industryIfOthers:
            values?.industry === OTHERS
              ? values?.industryIfOthers || OTHERS
              : "",
          companyName: values?.companyName || "",
          companyAddress: values?.companyAddress || "",
          companyPinCode: values?.companyPinCode || "",
          designation: values?.designation || OTHERS,
          designationIfOthers: values?.designationIfOthers || "",
        });
        break;

      /** Employee - Government Sector **/
      case OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR:
        Object.assign(payload.professionalDetails, {
          departmentDivision: values?.departmentDivision || "",
          designation: values?.designation || OTHERS,
          designationIfOthers: values?.designationIfOthers || "",
          officeAddress: values?.officeAddress || "",
          officePinCode: values?.officePinCode || "",
        });
        break;

      /** Armed Forces **/
      case OCCUPATION.ARMED_FORCES:
        Object.assign(payload.professionalDetails, {
          branch: values?.branch || "",
          rank: values?.rank || "",
          officeAddress: values?.officeAddress || "",
          officePinCode: values?.officePinCode || "",
        });
        break;

      /** Business **/
      case OCCUPATION.BUSINESS:
        Object.assign(payload.professionalDetails, {
          industry: values?.industry || "",
          industryIfOthers:
            values?.industry === OTHERS
              ? values?.industryIfOthers || OTHERS
              : "",
          companyName: values?.companyName || "",
          companyAddress: values?.companyAddress || "",
          companyPinCode: values?.companyPinCode || "",
        });
        break;

      /** Retired **/
      case OCCUPATION.RETIRED:
        // Only annualIncome already included
        break;

      /** Homemaker **/
      case OCCUPATION.HOMEMAKER:
        // Only annualIncome already included
        break;

      default:
        break;
    }

    return payload;
  };

  const handleSaveForm = async (type) => {
    if (Object.keys(professionalDetailFormik.errors).length === 0) {
      const payload = createProfessionalDetailsPayload(type === "saveForLater");

      try {
        const res = await dispatch(
          updateApplicantDetailsForm({
            path: `${route.UPDATE_VOUCHER_APPLICANT_DETAILS}/${voucherId}`,
            payload: payload,
            opportunityId: voucherId,
          })
        ).unwrap();

        if (res?.status === 200) {
          let completedSteps = {
            ...stepsCompleted,
            [activeStep]: true,
          };
          dispatch(setCheckFirstStepCompleted(false));
          dispatch(setEditPreviewApplicant(false));
          dispatch(setStepsCompleted(completedSteps));
          dispatch(setApplicantActiveStep(0));
          dispatch(setActiveStep(activeStep + 1));
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const handlePrevious = () => {
    dispatch(setApplicantActiveStep(applicantActiveStep - 1));
  };

  const saveUpdatedData = async () => {
    if (Object.keys(professionalDetailFormik.errors).length === 0) {
      const payload = createProfessionalDetailsPayload(false);

      await dispatch(
        updateApplicantDetailsForm({
          path: `${route.UPDATE_VOUCHER_APPLICANT_DETAILS}/${voucherId}`,
          payload: payload,
          opportunityId: voucherId,
        })
      );
    }
  };

  // Expose the save function to the parent component
  useImperativeHandle(ref, () => ({
    saveUpdatedData,
    errors: professionalDetailFormik.errors,
  }));
  const saveAndPreview = async (type) => {
    if (Object.keys(professionalDetailFormik.errors).length === 0) {
      const payload = {
        ...createProfessionalDetailsPayload(type === "saveForLater"),
        ...(isApplicantUpdateMode ? { isApplicantsUpdated: true } : {}),
      };

      await dispatch(
        updateApplicantDetailsForm({
          path: `${route.UPDATE_VOUCHER_APPLICANT_DETAILS}/${voucherId}`,
          payload: payload,
          opportunityId: voucherId,
        })
      )
        .unwrap()
        .then((res) => {
          if (res?.status === 200) {
            dispatch(setEditPreviewApplicant(false));
            history.push(
              isApplicantUpdateMode
                ? `/voucher-form/thank-you/${voucherId}`
                : `/customer-preview/${voucherId}`
            );

            let completedSteps = {
              ...stepsCompleted,
              [activeStep]: true,
            };
            dispatch(setStepsCompleted(completedSteps));
            dispatch(setApplicantActiveStep(0));
          }
        });
    }
  };

  useEffect(() => {
    if (applicantNo === 1) {
      dispatch(setApplicantActiveStep1(applicantActiveStep));
    }

    if (applicantNo === 2) {
      dispatch(setApplicantActiveStep2(applicantActiveStep));
    }

    return () => { };
  }, [applicantActiveStep, applicantNo, dispatch]);

  const handleAddApplicant = async (type) => {
    await professionalDetailFormik.validateForm();
    professionalDetailFormik.setTouched(
      Object.keys(professionalDetailFormik.initialValues).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      )
    );
    if (Object.keys(professionalDetailFormik.errors).length === 0) {
      const payload = createProfessionalDetailsPayload(type === "saveForLater");

      try {
        const res = await dispatch(
          updateApplicantDetailsForm({
            path: `${route.UPDATE_VOUCHER_APPLICANT_DETAILS}/${voucherId}`,
            payload: payload,
            opportunityId: voucherId,
          })
        ).unwrap();

        if (res?.status === 200) {
          let completedSteps = {
            ...stepsCompleted,
            [activeStep]: true,
          };
          dispatch(setApplicantTotal(applicantNo + 1));
          dispatch(setCheckFirstStepCompleted(false));
          dispatch(setStepsCompleted(completedSteps));
          dispatch(setApplicantActiveStep(0));
          dispatch(setActiveStep(activeStep + 1));
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const handleArmedPlaceChange = (data) => {
    professionalDetailFormik.setFieldValue(
      "officePinCode",
      data?.pinCode ?? ""
    );
  };

  const handlePlaceChange = (data) => {
    professionalDetailFormik.setFieldValue(
      "companyPinCode",
      data?.pinCode ?? ""
    );
  };

  const renderOccupationFields = (formik, masterData) => {
    const occupation = formik.values.occupation;
    const occupationFieldMap = {
      [OCCUPATION.ARMED_FORCES]: (
        <>
          <Grid item xs={12} sm={6}>
            <SelectInput
              field={{
                name: "branch",
                label: t("professionalDetails.fields.branch"),
                required: true,
                readonly: false,
                // options: masterData?.Branches || [],
                options: BRANCH_OPTIONS || [],
                placeholder: t("professionalDetails.placeholders.branch"),
              }}
              formik={formik}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextFieldInput
              field={{
                name: "rank",
                label: t("professionalDetails.fields.rank"),
                required: true,
                readonly: false,
                placeholder: t("professionalDetails.placeholders.rank"),
                handleOnChange: (e) => {
                  const newValue = e.target.value?.replaceAll(/[^a-zA-Z\s]/g, "");
                  formik.setFieldValue("rank", newValue);
                },
              }}
              formik={formik}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <GoogleMapsAutocomplete
              name="officeAddress"
              formik={professionalDetailFormik}
              label={t("professionalDetails.fields.officeAddress")}
              placeholder={t("professionalDetails.placeholders.officeAddress")}
              variant="outlined"
              TextFieldProps={{ size: "small" }}
              onSelect={(data) => handleArmedPlaceChange(data)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextFieldInput
              field={{
                name: "officePinCode",
                label: t("professionalDetails.fields.officePinCode"),
                placeholder: t(
                  "professionalDetails.placeholders.officePinCode"
                ),
                required: false,
                readonly: false,
                maxLength: 6,
                handleOnChange: (e) => {
                  const newValue = e.target.value
                    ?.replaceAll(/\D/g, "")
                    .slice(0, 6);
                  professionalDetailFormik.setFieldValue(
                    "officePinCode",
                    newValue
                  );
                },
              }}
              formik={professionalDetailFormik}
            />
          </Grid>
        </>
      ),
    };
    return occupationFieldMap[occupation] || null;
  };
  return (
    <div>
      <form
        onSubmit={professionalDetailFormik.handleSubmit}
        onReset={professionalDetailFormik.handleReset}
      >
        {" "}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SelectInput
                  field={{
                    name: "occupation",
                    label: t("professionalDetails.fields.occupation"),
                    required: true,
                    readonly: false,
                    options: masterData?.Occupation || [],
                    // options: OCCUPATION_OPTIONS || [],
                    placeholder: t(
                      "professionalDetails.placeholders.occupation"
                    ),
                  }}
                  formik={professionalDetailFormik}
                />
              </Grid>
            </Grid>
          </Grid>
          {/* Industry & company fields */}
          {professionalDetailFormik.values.occupation &&
            [OCCUPATION.EMPLOYED_PRIVATE_SECTOR, OCCUPATION.BUSINESS].includes(
              professionalDetailFormik.values.occupation
            ) && (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      marginBottom: "6px",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {t("professionalDetails.fields.industry")}{" "}
                    <span className="asteriskColor">*</span>
                  </Typography>
                  <SearchableAutoComplete
                    field={{
                      name: "industry",
                      otherField: "industryIfOthers",
                      options: masterData?.Industry || [],
                      placeholder: t(
                        "professionalDetails.placeholders.industry"
                      ),
                      othersValue: OTHERS,
                      required: true,
                      readonly: false,
                    }}
                    formik={professionalDetailFormik}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextFieldInput
                      field={{
                        name: "companyName",
                        label: t("professionalDetails.fields.companyName"),
                        placeholder: t(
                          "professionalDetails.placeholders.companyName"
                        ),
                        required: true,
                        readonly: false,
                        handleOnChange: (e) => {
                          const newValue = e.target.value?.replaceAll(/[^a-zA-Z0-9\s]/g, "");
                          professionalDetailFormik.setFieldValue(
                            "companyName",
                            newValue
                          );
                        },
                      }}
                      formik={professionalDetailFormik}
                    />
                    <Tooltip
                      title={t(
                        "professionalDetails.tooltips.addCompanyAddress"
                      )}
                    >
                      <AddCircleOutlineIcon
                        sx={{
                          color: "#1A407D",
                          marginTop:
                            professionalDetailFormik?.touched?.companyName &&
                              professionalDetailFormik?.errors?.companyName
                              ? "0px"
                              : "28px",
                        }}
                        onClick={() => setShowAddress(true)}
                      />
                    </Tooltip>
                  </Box>
                </Grid>

                {/* Address & Pincode */}
                {showAddress && (
                  <Grid item xs={12} sm={6}>
                    <GoogleMapsAutocomplete
                      name="companyAddress"
                      formik={professionalDetailFormik}
                      label={t("professionalDetails.fields.companyAddress")}
                      placeholder={t(
                        "professionalDetails.placeholders.companyAddress"
                      )}
                      variant="outlined"
                      TextFieldProps={{ size: "small" }}
                      onSelect={(data) => handlePlaceChange(data)}
                    />
                  </Grid>
                )}

                {showAddress && (
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      <TextFieldInput
                        field={{
                          name: "companyPinCode",
                          label: t("professionalDetails.fields.companyPinCode"),
                          placeholder: t(
                            "professionalDetails.placeholders.companyPinCode"
                          ),
                          required: false,
                          readonly: false,
                          maxLength: 6,
                          handleOnChange: (e) => {
                            const newValue = e.target.value
                              ?.replaceAll(/\D/g, "")
                              .slice(0, 6);
                            professionalDetailFormik.setFieldValue(
                              "companyPinCode",
                              newValue
                            );
                          },
                        }}
                        formik={professionalDetailFormik}
                      />
                      <RemoveCircleIcon
                        sx={{ color: "#ff0000", marginTop: "28px" }}
                        onClick={() => setShowAddress(false)}
                      />
                    </Box>
                  </Grid>
                )}
              </>
            )}

          {/* Designation only for salaried */}
          {(professionalDetailFormik.values.occupation ===
            OCCUPATION.EMPLOYED_PRIVATE_SECTOR ||
            professionalDetailFormik.values.occupation ===
            OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR) && (
              <>
                {" "}
                {professionalDetailFormik.values.occupation ===
                  OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <TextFieldInput
                          field={{
                            name: "departmentDivision",
                            label: t(
                              "professionalDetails.fields.departmentDivision"
                            ),
                            placeholder: t(
                              "professionalDetails.placeholders.departmentDivision"
                            ),
                            required: true,
                            readonly: false,
                            handleOnChange: (e) => {
                              const newValue = e.target.value?.replaceAll(/[^a-zA-Z\s]/g, "");
                              professionalDetailFormik.setFieldValue(
                                "departmentDivision",
                                newValue
                              );
                            },
                          }}
                          formik={professionalDetailFormik}
                        />
                      </Box>
                    </Grid>
                  )}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextFieldInput
                      field={{
                        name: "designationIfOthers",
                        label: t("professionalDetails.fields.designation"),
                        required: true,
                        readonly: false,
                        placeholder: t(
                          "professionalDetails.placeholders.designation"
                        ),
                        handleOnChange: (e) => {
                          const newValue = e.target.value?.replaceAll(/[^a-zA-Z\s]/g, "");
                          professionalDetailFormik.setFieldValue(
                            "designationIfOthers",
                            newValue
                          );
                        },
                      }}
                      formik={professionalDetailFormik}
                    />
                    {professionalDetailFormik?.values?.occupation ===
                      OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR && (
                        <Tooltip
                          title={t(
                            "professionalDetails.tooltips.addCompanyAddress"
                          )}
                        >
                          <AddCircleOutlineIcon
                            sx={{
                              color: "#1A407D",
                              marginTop:
                                professionalDetailFormik?.touched
                                  ?.departmentDivision &&
                                  professionalDetailFormik?.errors?.departmentDivision
                                  ? "0px"
                                  : "28px",
                            }}
                            onClick={() => setShowOfficeAddress(true)}
                          />
                        </Tooltip>
                      )}
                  </Box>
                </Grid>
                {showOfficeAddress &&
                  professionalDetailFormik.values.occupation ===
                  OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <GoogleMapsAutocomplete
                          name="officeAddress"
                          formik={professionalDetailFormik}
                          label={t("professionalDetails.fields.officeAddress")}
                          placeholder={t(
                            "professionalDetails.placeholders.officeAddress"
                          )}
                          variant="outlined"
                          TextFieldProps={{ size: "small" }}
                          onSelect={(data) => handleArmedPlaceChange(data)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            gap: 1,
                          }}
                        >
                          <TextFieldInput
                            field={{
                              name: "officePinCode",
                              label: t(
                                "professionalDetails.fields.officePinCode"
                              ),
                              placeholder: t(
                                "professionalDetails.placeholders.officePinCode"
                              ),
                              required: false,
                              readonly: false,
                              maxLength: 6,
                              handleOnChange: (e) => {
                                const newValue = e.target.value
                                  ?.replaceAll(/\D/g, "")
                                  .slice(0, 6);
                                professionalDetailFormik.setFieldValue(
                                  "officePinCode",
                                  newValue
                                );
                              },
                            }}
                            formik={professionalDetailFormik}
                          />
                          <RemoveCircleIcon
                            sx={{ color: "#ff0000", marginTop: "28px" }}
                            onClick={() => setShowOfficeAddress(false)}
                          />
                        </Box>
                      </Grid>
                    </>
                  )}
              </>
            )}
          {/* Armed Forces fields */}
          {renderOccupationFields(professionalDetailFormik, masterData)}

          {/* Annual Income */}
          {professionalDetailFormik.values.occupation && (
            <Grid item xs={12} sm={6}>
              <SelectInput
                field={{
                  name: "annualIncome",
                  label: t("professionalDetails.fields.annualIncome"),
                  options: masterData?.Annual_Household_Income_in_Lakhs,
                  placeholder: t(
                    "professionalDetails.placeholders.annualIncome"
                  ),
                  required: true,
                }}
                formik={professionalDetailFormik}
              />
            </Grid>
          )}

          <AddApplicantButton
            applicantNo={applicantNo}
            handleAddApplicant={handleAddApplicant}
            totalApplicants={applicantData?.noOfApplicants || applicantTotal}
            isOtherApplicant={false}
          />
        </Grid>
        <CommonButtonVoucherForm
          handlePrevious={handlePrevious}
          handleNext={handleSaveForm}
          innerSteps={true}
          applicantActiveStep={applicantActiveStep}
          // isDisable={
          //   !(
          //     professionalDetailFormik?.isValid &&
          //     professionalDetailFormik?.dirty
          //   )
          // }
          saveAndPreview={saveAndPreview}
          fromPage={"professional"}
          applicantNo={applicantNo}
          saveDraftData={createProfessionalDetailsPayload(true)}
        />
      </form>
    </div>
  );
});



ProfessionalDetails.propTypes = {
  applicantNo: PropTypes.any,
};

export default ProfessionalDetails;